import { useMemo } from 'react'
import { StreamContainer } from 'src/Features/SignalEarthFaults/StreamView/StreamContainer'
import { BenderStreamCard } from 'src/Features/SignalEarthFaults/StreamView/BenderStream/BenderStreamCard'
import { BenderSelector } from 'src/Features/SignalEarthFaults/StreamView/BenderStream/BenderSelector'
import type { AssetWithAlarms } from 'src/Types/AssetWithAlarms'
import type { Bender } from 'src/Types/Bender'
import { useTranslation } from 'react-i18next'
import { LocationFeatures, useLocationFeature } from 'src/Hooks/useLocationFeatures'

type OwnProps = {
  bendersWithAlarms: AssetWithAlarms[]
}

const sortBenders = (benders: Bender[], unsafely = false) =>
  benders.sort((a, b) => {
    // By default compare name if same baneDataLocationId
    if (a.baneDataLocationId === b.baneDataLocationId) {
      return unsafely ? a.name.localeCompare(b.name) : (a.name ?? '').localeCompare(b.name ?? '')
    }

    // Fallback to location name comparison
    return unsafely
      ? a.baneDataLocationName.localeCompare(b.baneDataLocationName)
      : (a.baneDataLocationName ?? '').localeCompare(b.baneDataLocationName ?? '')
  })

export const BenderStream = ({ bendersWithAlarms }: OwnProps) => {
  const { t } = useTranslation()
  const usingUnsafeSorting = useLocationFeature(LocationFeatures.UnsafeSorting)

  const benders = bendersWithAlarms.map(r => r.asset) as Bender[]
  const sortedBenders = useMemo(() => sortBenders(benders, usingUnsafeSorting), [benders, usingUnsafeSorting])
  const validBenderIds = useMemo(() => sortedBenders.map(bender => bender.baneDataId), [sortedBenders])

  return (
    <StreamContainer
      queryStateKey="benderIds"
      noneFoundText={t('benders.noneFound')}
      validIds={validBenderIds}
      renderSelectorComponent={({ pendingIds, onSelectionUpdated }) => (
        <BenderSelector benders={sortedBenders} pendingBenderIds={pendingIds} onSelectionUpdated={onSelectionUpdated} />
      )}
      renderContentComponent={({ selectedIds, dateRange }) => (
        <BenderStreamCard
          selectedFromDate={dateRange.fromDate}
          selectedToDate={dateRange.toDate}
          selectedBenders={sortedBenders.filter(bender => selectedIds.includes(bender.baneDataId))}
        />
      )}
    />
  )
}
